import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef } from "react";
import {
  ClientSideRowModelApiModule,
  ColDef,
  DomLayoutType,
  GridApi,
  GridOptions,
  TooltipModule,
  ValidationModule,
  themeQuartz,
} from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { provideGlobalGridOptions } from "ag-grid-community";
import { gridCellChanged, pageInitInf } from "components/utils/comm";
import { pageSizeSelect } from "components/utils/option";
import { IPageNav } from "interface";
import Pagination from "./pagination";
import CellRenderer from "./cellrender";
import { useTranslation } from "react-i18next";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./grid.scss";

provideGlobalGridOptions({
  theme: "legacy",
});

const myTheme = themeQuartz.withParams({
  // wrapperBorder: { style: "solid", width: 1, color: "#010101" },
  headerRowBorder: { color: "#010101" },
  rowHoverColor: "gray", // "#8d99ae",
  columnBorder: { style: "solid", width: 1, color: "#8d99ae" },
  fontSize: 12,
  cellTextColor: "#010101",
  oddRowBackgroundColor: "#fff",
  headerFontSize: 12,
});

ModuleRegistry.registerModules([
  AllCommunityModule,
  TooltipModule,
  ClientSideRowModelApiModule,
  ValidationModule,
]);

export interface IProps {
  theads: Array<any>;
  rowData: Array<any> | null;
  pagination?: boolean;
  filter?: boolean;
  editable?: boolean;
  rowHeight?: number;
  rowSelecte?: boolean;
  rowSelectMode?: "singleRow" | "multiRow";
  pageNav: IPageNav | null;
  pageSizeSelector?: Array<number>;
  domLayout?: DomLayoutType;
  alpineHeight?: number | string;
  rowClick?(params: any): void;
  cellstyle?(params: any): {};
  onCellChange?(props: any): void;
}

export default function Grid({
  theads,
  rowData,
  pageNav,
  pagination = true,
  filter = false,
  editable = false,
  rowHeight = 24,
  rowSelecte = false,
  rowSelectMode = "singleRow",
  domLayout = "normal",
  pageSizeSelector = pageSizeSelect,
  alpineHeight = 600,
  cellstyle,
  rowClick,
  onCellChange,
}: IProps) {
  const { t } = useTranslation();
  const gridRef = useRef<AgGridReact | null>(null);
  const gridApi = useRef<GridApi | null>(null); // GridApi를 useRef로 저장
  const gridColumnApi = useRef<any | null>(null); // ColumnApi를 useRef로 저장

  const columnDefs: ColDef[] = theads.map((thead) => {
    const cellClass =
      "text-center border-b-1" +
      " " +
      (thead.type === "price" ? "text-right" : " ") +
      " " +
      "flex col justify-center items-center" +
      thead.styles;

    // const cellClass = `text-center border-b-1 ${
    //   thead.typpe === "price" ? "text-right" : " "
    // } flex col justify-center items-center ${thead.styles ?? ""}`;

    const pinned =
      thead.type === "id"
        ? "left"
        : thead.type === "uYn-btn" ||
          thead.type === "detail" ||
          thead.type === "modify"
        ? "right"
        : undefined;

    return {
      field: thead.field,
      suppressSizeToFit: true,
      editable: thead.type === "editable",
      hide: thead.type === "hide",
      headerName: t(thead.langNm),
      maxWidth: thead.width,
      type: thead.type,
      pinned: pinned,
      flex: thead.width ? 0 : 1,
      autoHeight:
        thead.type === "img" || thead.type === "img-modify" ? true : false,
      // flex: thead.flex,
      cellStyle: (params) => {
        const cellstl = cellstyle && cellstyle(params);
        const defaultStyle = {
          textAlign: thead.type === "price" ? "right" : "center",
          fontWeight: thead.type === "btn" ? "bold" : "",
          fontSize: 12,
          padding: 0,
          paddingRight: 8,
          paddingLeft: 8,
        };
        return { ...defaultStyle, ...cellstl };
      },
      cellClass: cellClass,
      onCellClicked: thead.click,
      cellRendererParams: {},
      cellRenderer: CellRenderer,
    };
  });

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      // flex: 1,
      cellClass: "number-cell text-xs",
      filter: filter,
      minWidth: 100,
      headerClass: "text-center",
      editable: editable, // cell 수정 여부
      headerTooltip: "",
      resizable: true,
      sortable: false,
      autoHeight: true,
    };
  }, []);

  const gridOptions: GridOptions = {
    defaultColDef: {
      // minWidth: 100,
      // flex: 1,
    },
    groupHeaderHeight: 24,
    rowHeight: rowHeight,
    rowData: null, // 빈 데이터 배열을 예시로 사용
    overlayLoadingTemplate: `<span class="font-bold text-gray-600 pt-5">${t(
      "grid_search"
    )}</span>`, // 데이터가 로딩되기 전 표시할 오버레이
    overlayNoRowsTemplate: `<span class="font-bold text-red-500">${t(
      "grid_search_result"
    )}</span>`, // 데이터가 없을 때 표시할 오버레이
    headerHeight: 24,
    // rowSelection: "single",
  };

  //셀이 변경될 때마다 변경된 이미지 값을 처리할수 있습니다.
  const onCellValueChanged = (event: any) => {
    const { data, colDef, oldValue, newValue } = event;
    gridCellChanged({
      changeValue: {
        oldValue,
        newValue,
        data,
        colDef,
      },
    });
  };

  // // checkbox
  // const rowSelection: RowSelectionOptions = {
  //   mode: rowSelectMode,
  //   headerCheckbox: rowSelectMode === "multiRow" ? true : false,
  // };

  const onGridReady = (params: any) => {
    gridApi.current = params.api; // gridApi 객체 초기화
    gridColumnApi.current = params.columnApi; // columnApi 객체 초기화
  };
  return (
    <div>
      <div className="ag-theme-alpine" style={{ height: alpineHeight }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={pageInitInf.pageRows}
          paginationPageSizeSelector={pageSizeSelector}
          // onPaginationChanged={onPaginationChanged}
          onCellValueChanged={onCellValueChanged} //셀값이 변경될떼 처리
          onGridReady={onGridReady}
          // paginateChildRows
          gridOptions={gridOptions}
          enableCellTextSelection={true}
          detailCellRendererParams={"myCellRendererComp"}
          rowStyle={{ background: "white" }}
          rowSelection={"single"}
          // rowSelection={rowSelecte ? rowSelection : undefined}
          // domLayout="normal"
          domLayout={domLayout}
          theme={myTheme}
        />
      </div>
      {pageNav && rowData && rowData.length !== 0 && pagination && (
        <Pagination gridApi={gridApi} pageNavInf={pageNav} />
      )}
    </div>
  );
}
